import React, { useState } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Alert, 
  Steps, 
  Progress, 
  Space,
  Divider,
  Tag,
  Statistic,
  message,
  Modal,
  Table,
  Descriptions,
  DatePicker,
  Tree,
  Badge,
  Select,
  Collapse,
  Input
} from 'antd';
import {
  CloudUploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ImportOutlined,
  WarningOutlined,
  CalendarOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  EnvironmentOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { bulkImportService } from '../../services/bulkImportService';
import './BulkDataImport.css';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;
const { Option } = Select;

const BulkDataImport = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(dayjs());
  const [importPreview, setImportPreview] = useState(null);
  const [hierarchyPreview, setHierarchyPreview] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [editingConflicts, setEditingConflicts] = useState(new Map());
  const [resolvedConflicts, setResolvedConflicts] = useState(new Map());
  const [validationConflicts, setValidationConflicts] = useState([]);
  const [importProgress, setImportProgress] = useState({
    step: '',
    message: '',
    percent: 0,
    isImporting: false,
    isComplete: false,
    results: null,
    errors: []
  });

  // File upload and parsing
  const handleFileUpload = async (info) => {
    const { file } = info;
    
    if (file.status === 'uploading') {
      setLoading(true);
      setProgress(25);
      return;
    }

    if (file.status === 'done') {
      setProgress(50);
      
      try {
        // Process the file using our service
        const result = await bulkImportService.processFile(file.originFileObj || file);
        
        if (result.success) {
          message.success(`${file.name} processed successfully - ${result.summary.validRecords} valid records found`);
          setUploadedFile(file);
          setParseResult(result);
          
          // Generate import preview
          if (result.summary.validRecords > 0) {
            const preview = bulkImportService.prepareImportPreview(result);
            setImportPreview(preview);
            
            // Generate hierarchy preview
            const hierarchy = bulkImportService.buildJurisdictionHierarchy(preview.jurisdictions);
            setHierarchyPreview(hierarchy);
            
            // Auto-expand first level
            setExpandedKeys(['states']);
          }
          
          setCurrentStep(1);
          setProgress(100);
        } else {
          message.error(`Failed to process ${file.name}: ${result.error}`);
          setProgress(0);
        }
      } catch (error) {
        message.error(`Error processing file: ${error.message}`);
        setProgress(0);
      } finally {
        setLoading(false);
      }
    } else if (file.status === 'error') {
      setLoading(false);
      setProgress(0);
      message.error(`${file.name} upload failed`);
    }
  };

  const beforeUpload = (file) => {
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                    file.type === 'application/vnd.ms-excel' ||
                    file.name.endsWith('.xlsx') || 
                    file.name.endsWith('.xls');
    
    if (!isCSV && !isExcel) {
      message.error('You can only upload CSV or Excel files!');
      return false;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB!');
      return false;
    }
    
    return true;
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    onChange: handleFileUpload,
    beforeUpload,
    showUploadList: false,
    customRequest: ({ file, onSuccess, onError }) => {
      // Custom upload logic - simulate file reading
      setTimeout(() => {
        try {
          onSuccess("ok");
        } catch (error) {
          onError(error);
        }
      }, 1000);
    }
  };

  const resetImport = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setParseResult(null);
    setProgress(0);
    setImportPreview(null);
    setHierarchyPreview(null);
    setExpandedKeys([]);
    setEditingConflicts(new Map());
    setResolvedConflicts(new Map());
    setEffectiveDate(dayjs());
    setImportProgress({
      step: '',
      message: '',
      percent: 0,
      isImporting: false,
      isComplete: false,
      results: null,
      errors: []
    });
  };

  // Build tree data for hierarchy visualization
  const buildTreeData = () => {
    if (!hierarchyPreview) return [];

    const treeData = [{
      title: 'United States',
      key: 'US',
      icon: <EnvironmentOutlined />,
      children: []
    }];

    // Add states
    const statesNode = {
      title: `States (${hierarchyPreview.states.size})`,
      key: 'states',
      icon: <FolderOutlined />,
      children: []
    };

    hierarchyPreview.states.forEach((state, stateId) => {
      const stateNode = {
        title: (
          <Space>
            <Text strong>{state.stateName} ({state.state})</Text>
            <Badge count="New" style={{ backgroundColor: '#52c41a' }} />
          </Space>
        ),
        key: stateId,
        icon: <EnvironmentOutlined />,
        children: []
      };

      // Add counties for this state
      const stateCounties = Array.from(hierarchyPreview.counties.values())
        .filter(county => county.state === state.state);
      
      if (stateCounties.length > 0) {
        const countiesNode = {
          title: `Counties (${stateCounties.length})`,
          key: `${stateId}_counties`,
          icon: <FolderOutlined />,
          children: []
        };

        stateCounties.forEach(county => {
          const hasConflict = hierarchyPreview.conflicts.some(c => 
            c.type === 'county_mismatch' && c.existing === county.county
          );

          const countyNode = {
            title: (
              <Space>
                <Text style={{ color: hasConflict ? '#ff4d4f' : 'inherit' }}>
                  {county.county}
                </Text>
                {hasConflict && <EditOutlined style={{ color: '#ff4d4f' }} />}
                <Badge count="New" style={{ backgroundColor: '#52c41a' }} />
              </Space>
            ),
            key: county.id,
            icon: <EnvironmentOutlined />,
            isConflict: hasConflict,
            data: county,
            children: []
          };

          // Add cities for this county
          const countyCities = Array.from(hierarchyPreview.cities.values())
            .filter(city => city.state === county.state && city.county === county.county);

          countyCities.forEach(city => {
            const cityNode = {
              title: (
                <Space>
                  <Text>{city.city}</Text>
                  <Badge count="New" style={{ backgroundColor: '#52c41a' }} />
                </Space>
              ),
              key: city.id,
              icon: <EnvironmentOutlined />,
              data: city,
              children: []
            };

            // Add ZIP codes for this city
            const cityZips = Array.from(hierarchyPreview.zipCodes.values())
              .filter(zip => zip.state === city.state && zip.county === city.county && zip.city === city.city);

            cityZips.forEach(zip => {
              const zipNode = {
                title: (
                  <Space>
                    <Text>{zip.zipCode}</Text>
                    <Text type="secondary">({zip.salesTaxRates?.total}%)</Text>
                    <Badge count="New" style={{ backgroundColor: '#52c41a' }} />
                  </Space>
                ),
                key: zip.id,
                icon: <EnvironmentOutlined />,
                data: zip,
                isLeaf: true
              };
              cityNode.children.push(zipNode);
            });

            countyNode.children.push(cityNode);
          });

          countiesNode.children.push(countyNode);
        });

        stateNode.children.push(countiesNode);
      }

      statesNode.children.push(stateNode);
    });

    treeData[0].children.push(statesNode);
    return treeData;
  };

  // Handle conflict resolution
  const handleConflictEdit = (nodeKey, field, value) => {
    const newEditingConflicts = new Map(editingConflicts);
    if (!newEditingConflicts.has(nodeKey)) {
      newEditingConflicts.set(nodeKey, {});
    }
    newEditingConflicts.get(nodeKey)[field] = value;
    setEditingConflicts(newEditingConflicts);
  };

  const handleConflictSave = (nodeKey) => {
    const editData = editingConflicts.get(nodeKey);
    if (editData) {
      const newResolvedConflicts = new Map(resolvedConflicts);
      newResolvedConflicts.set(nodeKey, editData);
      setResolvedConflicts(newResolvedConflicts);
      
      const newEditingConflicts = new Map(editingConflicts);
      newEditingConflicts.delete(nodeKey);
      setEditingConflicts(newEditingConflicts);
      
      message.success('Conflict resolved successfully');
    }
  };

  const handleConflictCancel = (nodeKey) => {
    const newEditingConflicts = new Map(editingConflicts);
    newEditingConflicts.delete(nodeKey);
    setEditingConflicts(newEditingConflicts);
  };


  // Execute the import process
  const executeImport = async () => {
    setCurrentStep(2);
    setImportProgress({
      step: 'starting',
      message: 'Starting import process...',
      percent: 0,
      isImporting: true,
      isComplete: false,
      results: null,
      errors: []
    });

    try {
      // Execute the import with progress tracking (geocoding now handled internally)
      const importResult = await bulkImportService.executeImport(
        parseResult,
        effectiveDate.toDate(),
        { bulkAction: 'updateIfDifferent' }, // Add default conflict resolution
        (progress) => {
          setImportProgress(prev => ({
            ...prev,
            step: progress.step,
            message: progress.message,
            percent: progress.percent
          }));
        }
      );

      if (importResult.success) {
        // Check for validation conflicts (unknown counties, geocoding failures)
        const conflicts = await analyzeValidationConflicts(importResult);
        setValidationConflicts(conflicts);
        
        setImportProgress({
          step: 'completed',
          message: 'Import completed - validating geocoding results...',
          percent: 100,
          isImporting: false,
          isComplete: true,
          results: importResult,
          errors: []
        });
        
        // Move to validation step if there are conflicts, otherwise skip
        if (conflicts.length > 0) {
          setCurrentStep(3);
        } else {
          message.success('Import completed with no validation issues!');
        }
      } else {
        setImportProgress({
          step: 'failed',
          message: `Import failed: ${importResult.error}`,
          percent: 0,
          isImporting: false,
          isComplete: false,
          results: null,
          errors: [{ message: importResult.error, type: 'general' }]
        });
      }
    } catch (error) {
      setImportProgress({
        step: 'failed',
        message: `Import failed: ${error.message}`,
        percent: 0,
        isImporting: false,
        isComplete: false,
        results: null,
        errors: [{ message: error.message, type: 'general' }]
      });
    }
  };

  const steps = [
    {
      title: 'Upload File',
      status: currentStep === 0 ? 'process' : currentStep > 0 ? 'finish' : 'wait',
      icon: <CloudUploadOutlined />
    },
    {
      title: 'Data Preview',
      status: currentStep === 1 ? 'process' : currentStep > 1 ? 'finish' : 'wait',
      icon: <EyeOutlined />
    },
    {
      title: 'Import Progress',
      status: currentStep === 2 ? 'process' : currentStep > 2 ? 'finish' : 'wait',
      icon: <ImportOutlined />
    },
    {
      title: 'Validation & Conflicts',
      status: currentStep === 3 ? 'process' : currentStep > 3 ? 'finish' : 'wait',
      icon: <EditOutlined />
    }
  ];

  // Create validation summary for display
  const getValidationSummary = () => {
    if (!parseResult) return null;
    return bulkImportService.formatValidationSummary(parseResult);
  };

  // Create error table columns
  const errorColumns = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 60
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (text) => <Text type="danger">{text}</Text>
    },
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      render: (data) => (
        <Text code style={{ fontSize: '12px' }}>
          {typeof data === 'object' ? JSON.stringify(data) : String(data)}
        </Text>
      )
    }
  ];

  // Analyze validation conflicts after import completion
  const analyzeValidationConflicts = async (importResult) => {
    const conflicts = [];
    
    if (importResult && importResult.results) {
      const { jurisdictions = [], summary = {} } = importResult.results;
      
      // Find jurisdictions with unknown counties or failed geocoding
      jurisdictions.forEach(jurisdiction => {
        if (!jurisdiction.county || 
            jurisdiction.county === 'UNKNOWN' || 
            jurisdiction.county === 'PENDING' ||
            jurisdiction.county.toLowerCase().includes('unknown')) {
          
          conflicts.push({
            id: jurisdiction.id,
            type: 'unknown_county',
            jurisdiction,
            zipCode: jurisdiction.zipCode,
            city: jurisdiction.city,
            state: jurisdiction.state,
            originalCounty: jurisdiction.county,
            googleData: jurisdiction.googleData,
            suggestions: [], // Will be populated with manual geocoding if needed
            status: 'pending_review'
          });
        }
      });
      
      // Check for geocoding failures in summary
      if (summary.geocodingFailed > 0) {
        console.log(`Found ${summary.geocodingFailed} geocoding failures requiring validation`);
      }
    }
    
    return conflicts;
  };

  // Handle conflict resolution
  const handleConflictResolution = (conflictId, resolution) => {
    setResolvedConflicts(prev => new Map(prev.set(conflictId, resolution)));
  };

  // Apply all conflict resolutions
  const applyConflictResolutions = async () => {
    // Implementation for applying resolved conflicts
    message.success(`Applied ${resolvedConflicts.size} conflict resolutions`);
    setCurrentStep(4); // Move to completion
  };

  return (
    <div className="bulk-data-import">
      <div className="page-header">
        <Title level={2}>
          <CloudUploadOutlined /> Bulk Data Import
        </Title>
        <Paragraph>
          Import sales tax data from CSV/Excel files to automatically create tax rules and jurisdictions.
          Supports hierarchical jurisdiction creation and conflict resolution.
        </Paragraph>
      </div>

      <Card className="import-progress-card">
        <Steps current={currentStep} items={steps} />
      </Card>

      {currentStep === 0 && (
        <Card title="Upload Tax Data File" className="upload-card">
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Dragger {...uploadProps} className="upload-dragger">
                <p className="ant-upload-drag-icon">
                  <FileExcelOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support CSV and Excel files up to 10MB. Files should contain sales tax data 
                  with columns for ZIP codes, jurisdictions, and tax rates.
                </p>
              </Dragger>

              {loading && (
                <div style={{ marginTop: 16 }}>
                  <Progress percent={progress} status="active" />
                  <Text type="secondary">Uploading file...</Text>
                </div>
              )}
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Supported File Formats" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Tag color="blue">CSV</Tag>
                    <Text>Comma-separated values (.csv)</Text>
                  </div>
                  <div>
                    <Tag color="green">Excel</Tag>
                    <Text>Excel workbook (.xlsx, .xls)</Text>
                  </div>
                </Space>
              </Card>

              <Card title="Expected Data Format" size="small" style={{ marginTop: 16 }}>
                <Paragraph style={{ fontSize: 12 }}>
                  <strong>Required columns:</strong>
                  <br />• State (e.g., AR, CA, TX)
                  <br />• ZIP Code (e.g., 71601, 90210)  
                  <br />• City Name (e.g., Pine Bluff)
                  <br />• Total Tax Rate (e.g., 0.09375)
                </Paragraph>
                <Paragraph style={{ fontSize: 12 }}>
                  <strong>Optional columns:</strong>
                  <br />• County Name
                  <br />• State Rate, Local Rate, District Rate
                </Paragraph>
              </Card>
            </Col>
          </Row>

          <Alert
            message="Sample Data Available"
            description="Arkansas sales tax data (706 records) is available in the scripts folder for testing."
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />
        </Card>
      )}

      {currentStep === 1 && uploadedFile && parseResult && (
        <Card 
          title="Data Validation & Import Preview" 
          className="preview-card"
          extra={
            <Space>
              <Button onClick={() => setPreviewVisible(true)}>
                <EyeOutlined /> View Details
              </Button>
              <Button onClick={resetImport}>
                Upload Different File
              </Button>
            </Space>
          }
        >
          {/* File Information */}
          <Descriptions title="File Information" bordered size="small" column={3}>
            <Descriptions.Item label="Name">{parseResult.fileInfo.name}</Descriptions.Item>
            <Descriptions.Item label="Size">{`${(parseResult.fileInfo.size / 1024).toFixed(1)} KB`}</Descriptions.Item>
            <Descriptions.Item label="Type">{parseResult.fileInfo.type || 'CSV'}</Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Effective Date Selection */}
          <div style={{ marginBottom: 24 }}>
            <Space align="center">
              <CalendarOutlined />
              <Text strong>Effective Date:</Text>
              <DatePicker 
                value={effectiveDate}
                onChange={setEffectiveDate}
                format="YYYY-MM-DD"
                style={{ width: 200 }}
              />
              <Text type="secondary">Tax rates will be effective from this date</Text>
            </Space>
          </div>

          <Divider />

          {/* Validation Statistics */}
          <Row gutter={[24, 24]} className="data-stats-row">
            {getValidationSummary()?.stats.map((stat, index) => (
              <Col xs={12} lg={6} key={index}>
                <Statistic 
                  title={stat.label}
                  value={stat.value}
                  valueStyle={{ 
                    color: stat.type === 'success' ? '#3f8600' : 
                           stat.type === 'error' ? '#cf1322' : '#1890ff' 
                  }}
                />
              </Col>
            ))}
          </Row>

          {/* Import Preview Statistics */}
          {importPreview && parseResult.summary.validRecords > 0 && (
            <>
              <Divider />
              <Title level={5}>Import Preview</Title>
              <Row gutter={[16, 16]}>
                <Col xs={12} md={8}>
                  <Statistic 
                    title="Jurisdictions to Create" 
                    value={importPreview.summary.jurisdictionsToCreate}
                    suffix={`(${importPreview.summary.uniqueStates} states)`}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic 
                    title="Sales Tax Rules" 
                    value={importPreview.summary.salesTaxRulesToCreate}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic 
                    title="Requires Geocoding" 
                    value={importPreview.summary.needsGeocoding}
                    suffix="ZIP codes"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
              
              {importPreview.summary.needsGeocoding > 0 && (
                <Alert
                  message="Geocoding Required"
                  description={`${importPreview.summary.needsGeocoding} ZIP codes need county information from Google Maps API. This will be processed during import.`}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </>
          )}

          {/* Success/Error Summary */}
          {parseResult.summary.errorRecords > 0 ? (
            <Alert
              message={`Found ${parseResult.summary.errorRecords} validation errors`}
              description={`${parseResult.summary.validRecords} records are valid and ready for import. Please review errors below.`}
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Alert
              message="All records validated successfully!"
              description={`${parseResult.summary.validRecords} records are ready for import.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Error Details */}
          {parseResult.errors.length > 0 && (
            <Card title="Validation Errors" size="small" style={{ marginBottom: 16 }}>
              <Table
                columns={errorColumns}
                dataSource={parseResult.errors.slice(0, 10)}
                pagination={false}
                size="small"
                rowKey="row"
                scroll={{ x: true }}
              />
              {parseResult.errors.length > 10 && (
                <Alert
                  message={`Showing first 10 of ${parseResult.errors.length} errors`}
                  type="info"
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>
          )}

          {/* Hierarchy Preview */}
          {hierarchyPreview && importPreview && parseResult.summary.validRecords > 0 && (
            <>
              <Divider />
              <Title level={5}>Jurisdiction Hierarchy Preview</Title>
              
              {/* Summary Statistics */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Statistic title="States" value={hierarchyPreview.states.size} />
                </Col>
                <Col span={6}>
                  <Statistic title="Counties" value={hierarchyPreview.counties.size} />
                </Col>
                <Col span={6}>
                  <Statistic title="Cities" value={hierarchyPreview.cities.size} />
                </Col>
                <Col span={6}>
                  <Statistic title="ZIP Codes" value={hierarchyPreview.zipCodes.size} />
                </Col>
              </Row>

              {/* Conflicts Alert */}
              {hierarchyPreview.conflicts.length > 0 && (
                <Alert
                  message={`${hierarchyPreview.conflicts.length} Conflicts Detected`}
                  description="Some jurisdictions have conflicting county information from geocoding. Please resolve these conflicts below."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Hierarchy Tree */}
              <Collapse defaultActiveKey={['hierarchy']} style={{ marginBottom: 16 }}>
                <Panel 
                  header={
                    <Space>
                      <FolderOpenOutlined />
                      <Text strong>Jurisdiction Hierarchy</Text>
                      {hierarchyPreview.conflicts.length > 0 && (
                        <Badge count={hierarchyPreview.conflicts.length} style={{ backgroundColor: '#ff4d4f' }} />
                      )}
                    </Space>
                  } 
                  key="hierarchy"
                >
                  <Tree
                    showIcon
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    treeData={buildTreeData()}
                    height={400}
                    style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 8 }}
                  />
                </Panel>
              </Collapse>

              {/* Conflict Resolution Section */}
              {hierarchyPreview.conflicts.length > 0 && (
                <Card title="Resolve Conflicts" size="small" style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    The following jurisdictions have conflicting county information. Please select the correct county name:
                  </Text>
                  
                  {hierarchyPreview.conflicts.map((conflict, index) => (
                    <div key={index} style={{ marginBottom: 16, padding: 12, border: '1px solid #ff7875', borderRadius: 6, backgroundColor: '#fff2f0' }}>
                      <Row gutter={16} align="middle">
                        <Col span={8}>
                          <Text strong>{conflict.city}, {conflict.zipCode}</Text>
                          <br />
                          <Text type="secondary">Conflict detected</Text>
                        </Col>
                        <Col span={8}>
                          <Text>County: </Text>
                          {editingConflicts.has(conflict.jurisdictionId) ? (
                            <Select
                              style={{ width: '100%' }}
                              value={editingConflicts.get(conflict.jurisdictionId)?.county || conflict.existing}
                              onChange={(value) => handleConflictEdit(conflict.jurisdictionId, 'county', value)}
                            >
                              <Option value={conflict.existing}>{conflict.existing} (Current)</Option>
                              <Option value={conflict.new}>{conflict.new} (Geocoded)</Option>
                              <Option value="custom">Custom...</Option>
                            </Select>
                          ) : (
                            <Text style={{ color: '#ff4d4f' }}>
                              {resolvedConflicts.get(conflict.jurisdictionId)?.county || conflict.existing}
                            </Text>
                          )}
                        </Col>
                        <Col span={8}>
                          {editingConflicts.has(conflict.jurisdictionId) ? (
                            <Space>
                              <Button 
                                type="primary" 
                                size="small" 
                                icon={<SaveOutlined />}
                                onClick={() => handleConflictSave(conflict.jurisdictionId)}
                              >
                                Save
                              </Button>
                              <Button 
                                size="small" 
                                icon={<CloseOutlined />}
                                onClick={() => handleConflictCancel(conflict.jurisdictionId)}
                              >
                                Cancel
                              </Button>
                            </Space>
                          ) : (
                            <Button 
                              type="link" 
                              icon={<EditOutlined />}
                              onClick={() => setEditingConflicts(new Map(editingConflicts.set(conflict.jurisdictionId, {})))}
                            >
                              {resolvedConflicts.has(conflict.jurisdictionId) ? 'Edit' : 'Resolve'}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                  ))}

                  {resolvedConflicts.size === hierarchyPreview.conflicts.length && (
                    <Alert
                      message="All conflicts resolved!"
                      description="You can now proceed with the import."
                      type="success"
                      showIcon
                      style={{ marginTop: 12 }}
                    />
                  )}
                </Card>
              )}
            </>
          )}

          <Space>
            <Button 
              type="primary" 
              onClick={executeImport}
              disabled={parseResult.summary.validRecords === 0 || (hierarchyPreview?.conflicts.length > 0 && resolvedConflicts.size < hierarchyPreview.conflicts.length)}
              loading={importProgress.isImporting}
            >
              {importProgress.isImporting ? 'Importing...' : 'Start Import'}
            </Button>
            <Button onClick={resetImport} disabled={importProgress.isImporting}>
              Start Over
            </Button>
          </Space>
        </Card>
      )}

      {currentStep === 2 && (
        <Card title="Import Progress" className="import-progress-card">
          {/* Progress Overview */}
          <div style={{ marginBottom: 24 }}>
            <Progress 
              percent={importProgress.percent}
              status={importProgress.step === 'failed' ? 'exception' : 'active'}
              strokeColor={importProgress.isComplete ? '#52c41a' : '#1890ff'}
            />
            <div style={{ marginTop: 8 }}>
              <Text strong>{importProgress.message}</Text>
            </div>
          </div>

          {/* Step Details */}
          {importProgress.step && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic 
                    title="Current Step" 
                    value={importProgress.step.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    prefix={
                      importProgress.isImporting ? <Progress type="circle" size={20} percent={100} showInfo={false} /> :
                      importProgress.isComplete ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                      importProgress.step === 'failed' ? <WarningOutlined style={{ color: '#ff4d4f' }} /> :
                      null
                    }
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Progress" 
                    value={`${importProgress.percent}%`}
                    valueStyle={{ color: importProgress.isComplete ? '#3f8600' : '#1890ff' }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* Import Results */}
          {importProgress.isComplete && importProgress.results && (
            <Card title="Import Summary" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic 
                    title="Jurisdictions Created" 
                    value={importProgress.results.summary?.jurisdictionsCreated || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    {importProgress.results.summary?.jurisdictionsExisting || 0} existing
                  </div>
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Sales Tax Rules Created" 
                    value={importProgress.results.summary?.salesTaxRulesCreated || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    {importProgress.results.summary?.salesTaxRulesExisting || 0} existing
                  </div>
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Geocoding Success" 
                    value={importProgress.results.summary?.geocodingSuccessful || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    {importProgress.results.summary?.geocodingFailed || 0} failed
                  </div>
                </Col>
              </Row>
              
              {/* Additional Details Row */}
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic 
                    title="Conflicts Resolved" 
                    value={resolvedConflicts.size}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Total Records Processed" 
                    value={importProgress.results.totalRecords || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Processing Time" 
                    value={importProgress.results.processingTime || '0s'}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
              </Row>

              {/* Success Actions */}
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Button type="primary" onClick={() => navigate('/dashboard/jurisdictions')}>
                    View Jurisdictions
                  </Button>
                  <Button onClick={() => navigate('/dashboard/sales-tax-rules')}>
                    View Sales Tax Rules
                  </Button>
                  <Button onClick={resetImport}>
                    Import Another File
                  </Button>
                </Space>
              </div>
            </Card>
          )}

          {/* Error Details */}
          {importProgress.errors.length > 0 && (
            <Card title="Import Errors" size="small" style={{ marginBottom: 16 }}>
              <Alert
                message={`${importProgress.errors.length} errors occurred during import`}
                description="Please review the errors below and consider retrying the failed operations."
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              {importProgress.errors.map((error, index) => (
                <div key={index} style={{ 
                  marginBottom: 12, 
                  padding: 12, 
                  border: '1px solid #ff7875', 
                  borderRadius: 6, 
                  backgroundColor: '#fff2f0' 
                }}>
                  <Text strong style={{ color: '#ff4d4f' }}>{error.type || 'Error'}:</Text>
                  <br />
                  <Text>{error.message}</Text>
                </div>
              ))}

              {/* Retry Options */}
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Button type="primary" danger onClick={executeImport}>
                    Retry Import
                  </Button>
                  <Button onClick={resetImport}>
                    Start Over
                  </Button>
                </Space>
              </div>
            </Card>
          )}

          {/* Loading State */}
          {importProgress.isImporting && (
            <Alert
              message="Import in Progress"
              description={importProgress.message}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Back to Preview */}
          {!importProgress.isImporting && !importProgress.isComplete && importProgress.errors.length === 0 && (
            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Back to Preview
              </Button>
              <Button onClick={resetImport}>
                Start Over
              </Button>
            </Space>
          )}
        </Card>
      )}

      {/* Step 3: Validation & Conflicts */}
      {currentStep === 3 && (
        <Card title="Validation & Conflict Resolution" className="validation-card">
          <Alert
            message={`Found ${validationConflicts.length} geocoding conflicts that need review`}
            description="Please review and resolve conflicts below before completing the import."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationConflicts.length > 0 && (
            <>
              <Table
                dataSource={validationConflicts}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: 'ZIP Code',
                    dataIndex: 'zipCode',
                    key: 'zipCode',
                    width: 100,
                    render: (text) => <Tag color="blue">{text}</Tag>
                  },
                  {
                    title: 'City',
                    dataIndex: 'city',
                    key: 'city',
                    width: 150,
                    ellipsis: true
                  },
                  {
                    title: 'State',
                    dataIndex: 'state',
                    key: 'state',
                    width: 80,
                    render: (text) => <Tag>{text}</Tag>
                  },
                  {
                    title: 'Current County',
                    dataIndex: 'originalCounty',
                    key: 'originalCounty',
                    width: 150,
                    render: (text) => (
                      <Tag color="red" icon={<WarningOutlined />}>
                        {text || 'UNKNOWN'}
                      </Tag>
                    )
                  },
                  {
                    title: 'Google Suggestion',
                    key: 'googleSuggestion',
                    width: 150,
                    render: (_, record) => {
                      if (record.googleData && record.googleData.address_components) {
                        const county = record.googleData.address_components.find(c => 
                          c.types.includes('administrative_area_level_2')
                        );
                        if (county) {
                          return (
                            <Tag color="green">
                              {county.long_name.replace(' County', '')}
                            </Tag>
                          );
                        }
                      }
                      return <Tag color="gray">No suggestion</Tag>;
                    }
                  },
                  {
                    title: 'Resolution',
                    key: 'resolution',
                    width: 200,
                    render: (_, record) => {
                      const currentResolution = resolvedConflicts.get(record.id);
                      
                      return (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Select
                            placeholder="Choose resolution"
                            style={{ width: '100%' }}
                            value={currentResolution?.type}
                            onChange={(value) => {
                              if (value === 'google') {
                                const county = record.googleData?.address_components?.find(c => 
                                  c.types.includes('administrative_area_level_2')
                                );
                                if (county) {
                                  handleConflictResolution(record.id, {
                                    type: 'google',
                                    value: county.long_name.replace(' County', ''),
                                    source: 'google_geocoding'
                                  });
                                }
                              } else if (value === 'manual') {
                                // Manual entry mode - will show input field
                                handleConflictResolution(record.id, {
                                  type: 'manual',
                                  value: '',
                                  source: 'manual'
                                });
                              } else if (value === 'skip') {
                                handleConflictResolution(record.id, {
                                  type: 'skip',
                                  value: 'UNKNOWN',
                                  source: 'skipped'
                                });
                              }
                            }}
                          >
                            <Option value="google">Use Google Suggestion</Option>
                            <Option value="manual">Enter Manually</Option>
                            <Option value="skip">Leave as Unknown</Option>
                          </Select>
                          
                          {currentResolution?.type === 'manual' && (
                            <Input
                              placeholder="Enter county name"
                              value={currentResolution.value}
                              onChange={(e) => {
                                handleConflictResolution(record.id, {
                                  ...currentResolution,
                                  value: e.target.value
                                });
                              }}
                            />
                          )}
                        </Space>
                      );
                    }
                  }
                ]}
              />

              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setCurrentStep(2)}>
                    Back to Import
                  </Button>
                  <Button 
                    type="primary"
                    onClick={applyConflictResolutions}
                    disabled={resolvedConflicts.size === 0}
                  >
                    Apply Resolutions ({resolvedConflicts.size})
                  </Button>
                </Space>
              </div>
            </>
          )}
        </Card>
      )}

      <Modal
        title="Import Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={1000}
      >
        {parseResult && (
          <div>
            <Descriptions title="Processing Summary" bordered size="small" column={2}>
              <Descriptions.Item label="Total Rows">{parseResult.summary.totalRows}</Descriptions.Item>
              <Descriptions.Item label="Valid Records">{parseResult.summary.validRecords}</Descriptions.Item>
              <Descriptions.Item label="Error Records">{parseResult.summary.errorRecords}</Descriptions.Item>
              <Descriptions.Item label="Success Rate">{parseResult.summary.successRate}%</Descriptions.Item>
            </Descriptions>

            {parseResult.records.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Sample Valid Records (First 5)</Title>
                <Table
                  columns={parseResult.headers.map(header => ({
                    title: header,
                    dataIndex: header,
                    key: header,
                    width: 120,
                    render: (text) => <Text style={{ fontSize: '12px' }}>{String(text)}</Text>
                  }))}
                  dataSource={parseResult.records.slice(0, 5)}
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                  rowKey={(record, index) => index}
                />
              </div>
            )}

            {parseResult.errors.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Validation Errors</Title>
                <Table
                  columns={errorColumns}
                  dataSource={parseResult.errors}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  rowKey="row"
                  scroll={{ x: true }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BulkDataImport;